/**
 * The two edges of the yard: the spruce hedge that shuts the right-hand line,
 * and the fenced paddock down the left.
 *
 * Both are hashed. They are what makes the yard a corridor rather than a
 * field, and the whole of the route's shape follows from them.
 */

import {
  cylinder,
  solid,
  type CylinderSpec,
  type MaterialKey,
  type SolidSpec,
} from '../world/Level';
import { VERAJA } from './levelPalju';
import { KAYTAVA } from './levelPirtti';

/* -------------------------------------------------------------- kuusikko - */

/**
 * ==========================================================================
 *  THE SPRUCE ALONG THE RIGHT-HAND EDGE
 * ==========================================================================
 *  Both obstacles in the yard are walls with a hole in them, and both of them
 *  used to have the same answer: ignore the hole and swing right. The yard is
 *  seventy metres deep on that side and there was nothing in any of it, so the
 *  ansalanka cost about seven metres to skip and the palju about nine - no
 *  threading, no cable, no Hannu, and no speed lost to any of it. An obstacle
 *  that is cheaper to walk round than to solve is scenery.
 *
 *  So the yard now ends where a yard ends: at the treeline. Two stands of
 *  spruce run the right-hand edge from the kivikasa by the lot to the front of
 *  the navetta, and each of them meets what it continues -
 *
 *      kivikasa -> kuusikko-ansalanka -> kottikärryt -> matkailuauto ->
 *      kuusikko-palju -> (the navetta's own yard)
 *
 *  - so the whole right-hand side is one unbroken thing rather than a row of
 *  posts with player-sized holes between them. `Level.test.ts` walks it and
 *  says so.
 *
 *  What it deliberately does *not* close is the line round the north side of
 *  the palju, which is where the klapiteline stands and therefore where the
 *  veräjä is bought (README, *Hannu ja palju*); `HANNU_PALJU` is a checkpoint
 *  that has to be crossable both ways round the tub. The stand stops three
 *  metres clear of the klapikasa, which is what turns that line into a
 *  corridor with two corners in it instead of an open field you can take at
 *  full vauhti.
 *
 *  Its east end does close something, though, and lately more than it used to:
 *  the palju's fence now runs into these trees rather than stopping short of
 *  them, so the corridor comes out at the veräjä and nowhere else. See
 *  `AIDAN_POHJOISPAA`.
 *
 *  A kuusi is why this works as a collider at all: its branches reach the
 *  ground, so the thing in the way is the whole skirt and not a trunk you can
 *  run past. The box is inscribed in the crown at head height, which is the
 *  only height a 1.72 m player ever meets it at.
 * ==========================================================================
 */
export const SPRUCE = {
  /** Side of the collider box. Its diagonal fits inside the crown at 1.72 m. */
  collider: 2.0,
  /** Crown radius where it meets the ground. */
  radius: 2.0,
  /**
   * Heights, walked in order and repeated. A stand of identical cones is a
   * fence with a texture on it; a stand of trees is not.
   */
  heights: [7.4, 6.6, 8.2, 7.0, 8.6, 6.9, 7.8, 6.4, 8.0, 7.2],
} as const;

interface Kuusikko {
  trunks: SolidSpec[];
  crowns: CylinderSpec[];
}

/**
 * `count` spruces evenly spaced from `from` to `to`, inclusive of both ends.
 *
 * The spacing is never allowed past `SPRUCE.collider`, which is what makes the
 * line solid: two axis-aligned boxes of side S whose centres are less than S
 * apart overlap on both axes whatever angle the line runs at, so there is no
 * gap to find and none to tune. Ask for too few and it throws at module load
 * rather than shipping a stand with a door in it.
 */
function spruceLine(
  prefix: string,
  from: { x: number; z: number },
  to: { x: number; z: number },
  count: number,
): Kuusikko {
  const spacing = Math.hypot(to.x - from.x, to.z - from.z) / (count - 1);
  if (spacing >= SPRUCE.collider) {
    throw new Error(`${prefix}: ${count} spruces leave a ${spacing.toFixed(2)} m gap`);
  }

  const trunks: SolidSpec[] = [];
  const crowns: CylinderSpec[] = [];
  const half = SPRUCE.collider / 2;
  for (let i = 0; i < count; i++) {
    const u = i / (count - 1);
    const x = from.x + (to.x - from.x) * u;
    const z = from.z + (to.z - from.z) * u;
    const name = `${prefix}-${i + 1}`;
    const height = SPRUCE.heights[i % SPRUCE.heights.length];
    // 1.8 m: over the player's head, so there is no seeing past it, and well
    // over the 0.71 m jump apex, so there is no hopping it either. It is also
    // low enough to stay inside the narrowest crown in the stand, which is
    // what keeps a drawn box from showing its corners through the branches.
    trunks.push(solid(name, 'foliage', x - half, z - half, x + half, z + half, 1.8));
    // The crown is decor and the box under it is the collider, the same split
    // the tornimajoitus uses: a cone cannot be collided with cheaply, and the
    // skirt of a spruce is close enough to a box that nobody can tell by
    // running into it. `topRadius` all but zero is what makes it a kuusi.
    crowns.push(
      cylinder(name, 'foliage', x, z, SPRUCE.radius + (i % 3) * 0.12, height, 0, {
        topRadius: 0.06,
        radialSegments: 10,
      }),
    );
  }
  return { trunks, crowns };
}

/**
 * Off the kivikasa's corner to the kottikärryt at the end of the ansalanka
 * row, which is the diagonal the lot's right-hand corner used to open onto. It
 * reaches a metre further east than it used to for one reason: the row moved,
 * and the barrow parked against the end of it moved with it. The chain along
 * this edge is only unbroken while each link still touches the next.
 */
export const KUUSIKKO_ANSALANKA = spruceLine('kuusikko-ansalanka', { x: 95.6, z: 2.9 }, { x: 106.0, z: -11.0 }, 10);

/**
 * Off the matkailuauto's tail to the front of the navetta, along the edge of
 * the gravel the cars turn round on - which is where a treeline would be, and
 * is also exactly far enough out to leave the palju's north side a corridor.
 */
export const KUUSIKKO_PALJU = spruceLine('kuusikko-palju', { x: 113.4, z: -13.9 }, { x: 128.4, z: -15.1 }, 10);

/**
 * Where the palju's fence ends in the north, which is inside that stand rather
 * than short of it. See `VERAJA`: the yard used to have a second way out here
 * and does not any more, so this is the number that has to be right.
 *
 * Read off the trees instead of typed, because what has to hold is not a
 * length but an overlap - the boards have to reach *into* the first kuusi
 * standing on their line, the way `aitaa` overlaps its own corners. Move the
 * stand and the fence follows it; move it off the line altogether and this
 * throws at module load rather than shipping a yard with a hole in it.
 */
export const AIDAN_POHJOISPAA = ((): number => {
  const onTheLine = KUUSIKKO_PALJU.trunks.filter(
    (kuusi) => kuusi.box.minX < VERAJA.x && kuusi.box.maxX > VERAJA.x,
  );
  if (onTheLine.length === 0) {
    throw new Error('paljun-aita: no kuusi on the fence line to bury the north end in');
  }
  // Past the northernmost of their south faces, so the end is inside every
  // tree on the line and not only inside the one that reaches furthest south.
  return Math.max(...onTheLine.map((kuusi) => kuusi.box.minZ)) + 0.35;
})();

/**
 * ==========================================================================
 *  THE TWO ENDS THE TREELINE USED TO STOP SHORT OF
 * ==========================================================================
 *  The two stands above close the yard's right-hand side between the kivikasa
 *  and the front of the navetta, and for a while that looked like the whole
 *  job. It was not. A boundary is worth what its ends are anchored to, and
 *  both of these ended in open grass - so the answer the kuusikko was built to
 *  delete came back the moment somebody looked one field further out.
 *
 *  ------------------------------------------------------------------------
 *   WEST: the lot had a right-hand side and no pieli to hold it on
 *  ------------------------------------------------------------------------
 *  On the left the boundary is fence, bay row, fence again: `AITAUS` ties the
 *  row's west end into the back field and its east end into the yard, and the
 *  twenty-one cars are only the middle of it. On the right there was the bay
 *  row and nothing at either end of it - and a row of cars with open grass off
 *  its corner is a row of cars you walk round.
 *
 *  Out of the ulko-ovi, hard left of the mouth of the aisle, and the whole
 *  parkkipaikka went past on the wrong side of it. That is fifty-five metres
 *  of slalom and the only stretch of the route long enough to actually fill
 *  the vauhti meter, declined for the price of one corner, on grass with
 *  nothing in it. Driven with the bottle it came in within a second of the
 *  reference line while touching nothing at all - which is not a shortcut with
 *  a price, it is the level being optional.
 *
 *  `KUUSIKKO_PARKIN_PIELI` is the pieli the left has had all along. Four
 *  spruces off the pirtti's own north-east corner through the empty first bay, so
 *  the row's west end is held the way the fence holds the other one. Trees
 *  rather than boards because this is the corner of Eino's yard, and because
 *  the thing it continues is a treeline.
 *
 *  ------------------------------------------------------------------------
 *   EAST: a treeline that stopped at the corner it should have turned at
 *  ------------------------------------------------------------------------
 *  `KUUSIKKO_PALJU` ended at the navetta's front corner, which is one step
 *  past the palju's checkpoint and not one step past that. Anyone already
 *  north of the treeline came round its end at x = 130 and dropped onto the
 *  porch from above - the ansalanka, the palju, Hannu, the klapi and the
 *  veräjä all behind them and none of them paid for.
 *
 *  So the treeline turns. It follows the field's own corner north instead of
 *  stopping at it, and the way round its end now comes out east of the
 *  navetta's front yard rather than on top of the door. What that line has to
 *  do to get back to the door is run the length of the pöytäryhmä (see
 *  `PITKA_POYTA`) - the apron the veräjä opens onto, taken from the far end
 *  and with the whole yard already behind it.
 *
 *  ------------------------------------------------------------------------
 *   WHAT IS DELIBERATELY LEFT OPEN
 *  ------------------------------------------------------------------------
 *  The right-hand line still exists. The yard's right-hand corner - between
 *  the last bay and the kivikasa - is a gap you can see from halfway down the
 *  lot, and nothing stops you taking it: you have run the parkkipaikka by the
 *  time you get there, and what you are declining from that corner on is the
 *  ansalanka and the palju. It is meant to be visible, and it is meant to be
 *  a decision rather than a wall.
 *
 *  It is just no longer free. Where it used to be worth about three seconds
 *  it is now the long side of the whole venue, and `Level.test.ts` drives both
 *  lines end to end and pins the difference rather than arguing it from
 *  distances on a map.
 * ==========================================================================
 */

/**
 * The lot's right-hand pieli: the pirtti's north-east corner into the tail of
 * bay 1, which is the corner the whole back field used to open off.
 *
 * The line starts *outside* the corner rather than on it, and that is the
 * whole of why the first of these four numbers is what it is. A kuusi is a 2 m
 * collider box under a crown 4 m across at the ground and 7 m tall, so it is a
 * much bigger thing than the corner it is being tied to: put the trunk on the
 * corner and a metre of invisible box stands in the käytävä, with the tree
 * itself growing up through the roof over it. That is a wall in the hallway
 * that nothing on screen accounts for, three metres from the ulko-ovi, in the
 * one place on the route where the run is already threading a doorway.
 *
 * So the trunk is placed by its box rather than by eye, and the box's
 * south-west corner is put exactly on the *outside* corner of the building:
 * `x = 33.0`, `z = KAYTAVA.northZ`. That is worth spelling out because it is
 * the only position that does both jobs at once -
 *
 *   - it overlaps `kaytava-paaty-b` and `kaytava-seina-pohjoinen` by the full
 *     0.3 m of `T` in each axis, so the treeline and the building share ground
 *     rather than meeting at a corner. A chain that only touches is a chain a
 *     0.68 m player can be squeezed through; this one has no slot in it.
 *   - and it puts nothing at all inside the corridor: the interior stops at
 *     `x = 33.0` and at `z = KAYTAVA.northZ`, which is where the box starts.
 *
 * The crown still reaches over the eave, and it is supposed to - that is a
 * spruce at the corner of a farmhouse, and the alternative is a gap between
 * the boundary and the thing it is anchored to. What it no longer does is
 * stand in the building.
 */
export const KUUSIKKO_PARKIN_PIELI = spruceLine(
  'kuusikko-parkin-pieli',
  { x: 34.0, z: KAYTAVA.northZ + 1.0 },
  { x: 38.2, z: 5.5 },
  4,
);

// Close the vacated bay to the next car with a gap narrower than the player.
// Only the last spruce moves; the others still anchor the pirtti corner.
const lastPieliTrunk = KUUSIKKO_PARKIN_PIELI.trunks.at(-1)!;
lastPieliTrunk.box.minX += 0.4;
lastPieliTrunk.box.maxX += 0.4;
KUUSIKKO_PARKIN_PIELI.crowns.at(-1)!.center[0] += 0.4;

// Render-only: move the first crown along the outward diagonal until its
// widest ring clears the corridor's inside corner. The collider stays joined
// to both walls; placing only the crown's axis outdoors still let branches in.
const pieliCrown = KUUSIKKO_PARKIN_PIELI.crowns[0];
const pieliTrunk = KUUSIKKO_PARKIN_PIELI.trunks[0].box;
pieliCrown.center = [
  pieliTrunk.minX + pieliCrown.radius / Math.SQRT2,
  pieliTrunk.minZ + pieliCrown.radius / Math.SQRT2,
];

/**
 * The turn. Off the last kuusi in front of the navetta, north up the far side
 * of the field, so the end there is to walk round is twenty metres further out
 * than the corner the treeline used to stop at - and thirty-four metres of
 * yard north of the door rather than fourteen.
 */
export const KUUSIKKO_KAARRE = spruceLine(
  'kuusikko-kaarre',
  { x: 129.6, z: -13.6 },
  { x: 156.0, z: 5.4 },
  19,
);

/* --------------------------------------------------- pihan vasen laita -- */

/**
 * ==========================================================================
 *  PIHAN VASEN LAITA - naapurin maatila
 * ==========================================================================
 *  The kuusikko above closes the right-hand side of the yard. This is the same
 *  argument on the left, and the left was the bigger hole of the two.
 *
 *  Every obstacle between the ulko-ovi and the palju is a wall with a gap in
 *  it, and all of them had the same third answer: go left. Left was forty
 *  metres of open grass running the whole length of the lot, so the
 *  parkkipaikka - fifty-five metres of slalom, and the only stretch on the
 *  route long enough to actually fill the vauhti meter - could be skipped by
 *  never entering it. An obstacle you can decline is not an obstacle, and a
 *  lot you can run past is scenery with cars in it.
 *
 *  So the grass belongs to somebody now. The venue is a farm already - the
 *  tornimajoitus is its old feed silo - and the field down the left-hand side
 *  is the neighbour's: a lato, a silo, a konehalli and a laidun, behind a
 *  board fence.
 *
 *  What makes it a boundary rather than dressing is the same thing that makes
 *  the kuusikko one: the chain is unbroken, and `Level.test.ts` walks it link
 *  by link.
 *
 *    aita-lansi -> across the pirtti's back field -> up the lot's west edge
 *    -> the lot's own left-hand bay row (nose-in at 2.4 m spacing, so the
 *       0.55 m between two cars is narrower than the 0.68 m player)
 *    -> down the far end of the lot -> along the yard past the ansalanka
 *    -> halkopino.
 *
 *  Two things it deliberately leaves open. The takapiha keeps its open ground:
 *  the fence is nineteen metres out from the käytävä's wall, seven further
 *  than anything the window line meets back there, so a scripted stumble still
 *  costs nothing. And the ansalanka keeps both of its lines - the fence meets
 *  the yard three metres south of the tolppa, so going round the bemari and
 *  over the cable is still a way round.
 *
 *  The fence is 1.45 m. Over the 0.71 m jump apex, so it is not hopped, and
 *  under eye height, so what is behind it is visible the whole way - which is
 *  the point of a fence rather than a hedge. The player has to be able to see
 *  that the left-hand side is somewhere they are not going, rather than find
 *  it out by running into it.
 * ==========================================================================
 */
export const AITAUS = {
  height: 1.45,
  /** Boards, so the thing has a thickness rather than a centre line. */
  thickness: 0.3,
  /** The back field's far side. Far enough out to leave the takapiha open. */
  takapihaZ: -19.0,
  /** The lot's west edge, where the fence turns and runs into the first bay. */
  parkkiX: 37.75,
  /** The far end of the lot, where it turns south again. */
  parkinPaatyX: 87.45,
  /** Along the yard, south of everything the ansalanka is made of. */
  pihaZ: -27.4,
  /** One step further south, which is what gets it under the halkopino. */
  halkopinoZ: -28.5,
} as const;

/**
 * One straight run of board fence between two points on the same axis.
 *
 * Both ends are extended by half the fence's own thickness, so two runs
 * meeting at a corner *overlap* rather than touch. A chain of boxes that only
 * touch is a chain with a corner in it, and a corner is exactly the place a
 * 0.68 m player gets squeezed through.
 */
function aitaa(
  name: string,
  from: { x: number; z: number },
  to: { x: number; z: number },
  material: MaterialKey = 'fence',
): SolidSpec {
  const half = AITAUS.thickness / 2;
  if (from.x === to.x) {
    const [minZ, maxZ] = from.z < to.z ? [from.z, to.z] : [to.z, from.z];
    return solid(name, material, from.x - half, minZ - half, from.x + half, maxZ + half, AITAUS.height);
  }
  if (from.z !== to.z) throw new Error(`${name}: a fence run has to follow an axis`);
  const [minX, maxX] = from.x < to.x ? [from.x, to.x] : [to.x, from.x];
  return solid(name, material, minX - half, from.z - half, maxX + half, from.z + half, AITAUS.height);
}

/**
 * The fence, in route order. The bay row is the missing middle: between
 * `aitaus-parkkipaikan-pieli` and `aitaus-parkkipaikan-paaty` the boundary is
 * the lot's own left-hand cars, which is why the two runs are buried in the
 * first and last of them rather than stopping short.
 */
export const AITAUS_KETJU: SolidSpec[] = [
  // West end buried in the blockout's own fence, so there is no end to round.
  aitaa('aitaus-takapiha-a', { x: -1.0, z: AITAUS.takapihaZ }, { x: 30.0, z: AITAUS.takapihaZ }),
  // The way onto the farm, shut. It is the one break in the boards, and it is
  // there so that the line reads as a fence somebody owns rather than as the
  // edge of the map - which is also what the sign on it is for.
  aitaa('aitaus-portti', { x: 30.0, z: AITAUS.takapihaZ }, { x: 32.0, z: AITAUS.takapihaZ }, 'wood'),
  aitaa('aitaus-takapiha-b', { x: 32.0, z: AITAUS.takapihaZ }, { x: AITAUS.parkkiX, z: AITAUS.takapihaZ }),
  // Up the lot's west edge and into the tail of bay 1. The overlap with that
  // car is the corner the whole thing exists to close: come out of the pirtti
  // and the only way on is north of it, into the mouth of the aisle.
  aitaa('aitaus-parkkipaikan-pieli', { x: AITAUS.parkkiX, z: AITAUS.takapihaZ }, { x: AITAUS.parkkiX, z: -6.75 }),
  // ... twenty-one bays of parked cars ...
  aitaa('aitaus-parkkipaikan-paaty', { x: AITAUS.parkinPaatyX, z: -18.6 }, { x: AITAUS.parkinPaatyX, z: AITAUS.pihaZ }),
  // One unbroken run past the ansalanka. There used to be a second gate in the
  // middle of it, because the tornimajoitus stood out in the field and somebody
  // had to be able to get to bed; the tornimajoitus is at the navetta's east
  // gable now, so the boards close.
  aitaa('aitaus-pihan-laita', { x: AITAUS.parkinPaatyX, z: AITAUS.pihaZ }, { x: 108.0, z: AITAUS.pihaZ }),
  aitaa('aitaus-pihan-kulma', { x: 108.0, z: AITAUS.pihaZ }, { x: 108.0, z: AITAUS.halkopinoZ }),
  aitaa('aitaus-halkopinolle', { x: 108.0, z: AITAUS.halkopinoZ }, { x: 118.2, z: AITAUS.halkopinoZ }),
];

/**
 * Rehupaalit at the top of the field, wrapped and stood on end the way they
 * are left in a Finnish field. Same split as the kuusikko: the drum is decor
 * and the box inscribed in it is the collider, because a cylinder is not
 * something collision here can be asked about cheaply.
 */
const PAALI = { radius: 0.75, height: 1.3 } as const;
const PAALI_PAIKAT: { x: number; z: number }[] = [
  { x: 77.4, z: -21.8 },
  { x: 79.6, z: -22.2 },
  { x: 76.2, z: -24.2 },
  { x: 78.4, z: -24.6 },
  { x: 80.6, z: -25.0 },
];

export const PAALIT = PAALI_PAIKAT.map((spot, index) => {
  // Inscribed in the drum's *twelve-sided* silhouette rather than in the circle
  // it approximates, and a hair shorter than it: a corner that pokes out
  // between two facets turns a bale into a crate at fifty metres.
  const half = 0.51;
  const name = `paali-${index + 1}`;
  return {
    box: solid(name, 'porcelain', spot.x - half, spot.z - half, spot.x + half, spot.z + half, PAALI.height - 0.03),
    drum: cylinder(name, 'porcelain', spot.x, spot.z, PAALI.radius, PAALI.height, 0, {
      radialSegments: 12,
    }),
  };
});

/**
 * The farmstead itself, out in the field behind the fence. None of it is on
 * any line the player can run, which is the whole point - it is there so that
 * the left-hand side is somewhere with a reason to be closed, and so that the
 * lot has something to look at over the roofs of the cars. The silo is the
 * tallest thing on the level after the lipputanko, and it is deliberately
 * placed halfway down the lot: from the aisle it is the one landmark that says
 * how far along you are.
 */
export const MAATILA: SolidSpec[] = [
  solid('lato', 'faluRed', 42.0, -30.0, 56.0, -21.0, 6.6),
  // The small one, out where the field starts. It is here rather than beside
  // the big one because this is the part of the view the pirtti's back yard
  // looks straight into: the whole left-hand side used to be a mown horizon.
  solid('heinalato', 'faluRed', 15.0, -32.5, 25.0, -25.5, 4.6),
  // Inscribed in the drum, exactly like the tornimajoitus it is a twin of.
  solid('siilo-runko', 'tower', 58.0, -26.5, 61.0, -23.5, 8.0),
  solid('konehalli', 'wood', 64.0, -34.0, 74.0, -28.0, 4.2),
  ...PAALIT.map((paali) => paali.box),
];
